import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEngagementApi } from "@/hooks/use-engagement-api"
import { useToast } from "@/hooks/use-toast"
import { navigateInParent } from "@/lib/app-messaging"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { engagementCreateSchema, type EngagementCreateFormData } from "@/lib/schemas/engagement-create-schema"

/**
 * Landing page for creating new engagements
 * 
 * Flow:
 * 1. User clicks "Create New" on report list (/next/report-azsearch/44)
 * 2. Navigates to /next/app/engagements/create
 * 3. User enters engagement name and code
 * 4. User clicks Create button
 * 5. Page calls POST /api/Engagement/with-data with name and code
 * 6. On success: Navigate to the new engagement details page
 * 7. On error: Show toast and stay on create page
 */
export default function EngagementCreatePage() {
  const engagementApi = useEngagementApi()
  const { toast } = useToast()
  const form = useForm<EngagementCreateFormData>({
    resolver: zodResolver(engagementCreateSchema),
    defaultValues: {
      engagementName: "",
      engagementCode: "",
    },
  })

  const handleCreate = async (data: EngagementCreateFormData) => {
    try {
      // POST /api/Engagement/with-data with name and code
      const response = await engagementApi.createEngagementWithData({
        coreInfo: {
          name: data.engagementName,
          code: data.engagementCode
        }
      })
      
      // Response structure: { data: { recordId: number, version: number, ... } }
      const recordId = response.data.recordId

      // Navigate to the new engagement details page
      navigateInParent(`/next/app/engagements/${recordId}`, { replaceUrl: true })
    } catch (err) {
      console.error('Failed to create engagement:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to create engagement'
      
      // Show error toast
      toast({
        title: "Failed to Create Engagement",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  const isCreating = form.formState.isSubmitting
  const isFormValid = form.formState.isValid

  return (
    <div className="min-h-screen bg-background">
      {/* Outer container with horizontal padding - contains header border */}
      <div className="px-6">
        {/* Header with bottom border */}
        <header className="py-4 border-b">
          {/* Title */}
          <h1 className="text-2xl font-semibold text-slate-900">
            New Engagement
          </h1>
        </header>
      </div>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleCreate)}>
            <div className="space-y-6 pt-4">
              <FormField
                control={form.control}
                name="engagementName"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <FormLabel className="text-sm font-medium text-slate-700 sm:w-48 sm:text-right flex-shrink-0">
                        Engagement Name <span className="text-destructive">*</span>
                      </FormLabel>
                      <div className="flex-1 max-w-md">
                        <FormControl>
                          <Input
                            {...field}
                            className="form-field-focus w-full"
                            placeholder=""
                            disabled={isCreating}
                          />
                        </FormControl>
                        <FormMessage className="mt-1" />
                      </div>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="engagementCode"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <FormLabel className="text-sm font-medium text-slate-700 sm:w-48 sm:text-right flex-shrink-0">
                        Engagement Code <span className="text-destructive">*</span>
                      </FormLabel>
                      <div className="flex-1 max-w-md">
                        <FormControl>
                          <Input
                            {...field}
                            className="form-field-focus w-full"
                            placeholder=""
                            disabled={isCreating}
                          />
                        </FormControl>
                        <FormMessage className="mt-1" />
                      </div>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            {/* Button positioned at bottom-right */}
            <div className="flex justify-end mt-8 pt-12">
              <Button
                type="submit"
                disabled={isCreating || !isFormValid}
                className="min-w-[180px]"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    {"Creating..."}
                  </>
                ) : (
                  "Create New Engagement"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </main>
    </div>
  )
}
